"use client";

import { motion } from "motion/react";
import Image from "next/image";

interface FeatureCardProps {
  variant: "primary" | "secondary" | "accent";
  icon: string;
  title: string;
  description: string;
  iconAlt?: string;
}

const variantStyles = {
  primary: {
    background: "bg-primary-light/40",
    textColor: "text-primary",
    shadow: "shadow-primary/10",
    animation: {
      initial: { opacity: 0, x: -30 },
      animate: { opacity: 1, x: 0 },
    },
  },
  accent: {
    background: "bg-accent-light/40",
    textColor: "text-accent",
    shadow: "shadow-accent/10",
    animation: { initial: { opacity: 0 }, animate: { opacity: 1 } },
  },
  secondary: {
    background: "bg-secondary-light/40",
    textColor: "text-secondary",
    shadow: "shadow-secondary/10",
    animation: {
      initial: { opacity: 0, x: 30 },
      animate: { opacity: 1, x: 0 },
    },
  },
};

export default function FeatureCard({
  variant,
  icon,
  title,
  description,
  iconAlt,
}: FeatureCardProps) {
  const styles = variantStyles[variant];

  return (
    <motion.div
      className="flex w-full max-w-70.5 flex-col justify-center"
      initial={styles.animation.initial}
      whileInView={styles.animation.animate}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.5 }}
    >
      <h3
        className={`${styles.textColor} z-2 flex min-h-max w-full items-center justify-center rounded-full px-2 py-2 ${styles.shadow} font-medium text-nowrap shadow-md ${styles.background}`}
      >
        {title}
      </h3>
      <div
        className={`${styles.background} relative -top-8 -mb-8 flex h-full flex-col items-center gap-6 rounded-[40px] px-3 pt-15 pb-6 shadow-md ${styles.shadow}`}
      >
        <Image
          width={210}
          height={140}
          src={icon}
          alt={iconAlt || `${title} icon`}
          className="h-35 w-auto"
          loading="lazy"
        />
        <p className={`text-center text-xl/relaxed`}>{description}</p>
      </div>
    </motion.div>
  );
}
